import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { 
  FiShield, FiFileText, FiInfo, FiArrowLeft, FiCheck, FiMail, FiGlobe, FiAlertCircle 
} from 'react-icons/fi';
import { motion } from 'framer-motion';

const LegalPages = ({ defaultTab = 'privacy', onBack, supportEmail }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);

  const tabs = [
    { id: 'privacy', label: 'Privacy Policy', icon: FiShield },
    { id: 'terms', label: 'Terms of Service', icon: FiFileText },
    { id: 'cookies', label: 'Cookie Policy', icon: FiInfo },
  ];

  const content = {
    privacy: (
      <div className="space-y-8">
        <div className="border-b border-slate-100 pb-6">
          <h2 className="text-3xl font-black text-slate-900 mb-2">Privacy Policy</h2>
          <p className="text-slate-500 text-sm italic">Last Revision: January 1, 2026 | Version 4.2.0</p>
        </div>
        <section className="space-y-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center">
            <span className="w-8 h-8 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center mr-3 text-sm">1</span>
            Data Collection & Identification
          </h3>
          <p className="text-slate-600 text-sm leading-relaxed">
            We collect specific information to provide a personalized training environment. This includes account information, professional profiles, and performance metrics.
          </p>
        </section>
        <section className="space-y-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center">
            <span className="w-8 h-8 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center mr-3 text-sm">2</span>
            Processing & Usage
          </h3>
          <p className="text-slate-600 text-sm leading-relaxed">
            Your data is processed for service delivery, verification of certifications, and platform optimization.
          </p>
        </section>
        <div className="p-8 bg-blue-600 rounded-[2rem] text-white text-center">
          <SafeIcon icon={FiMail} className="text-4xl mx-auto mb-4 opacity-50" />
          <h4 className="text-xl font-black mb-2">Legal Inquiries</h4>
          <p className="text-blue-100 text-sm mb-6">Have questions about your data or our terms?</p>
          <a href={`mailto:${supportEmail}`} className="inline-block bg-white text-blue-600 px-8 py-3 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-blue-50 transition-colors">
            Contact Compliance
          </a>
        </div>
      </div>
    ),
    terms: (
      <div className="space-y-8 text-slate-600">
        <div className="border-b border-slate-100 pb-6">
          <h2 className="text-3xl font-black text-slate-900 mb-2">Terms of Service</h2>
          <p className="text-slate-500 text-sm italic">Effective Date: January 1, 2026</p>
        </div>
        <p>By using this platform, you agree to follow our educational license and maintain the integrity of our certification process.</p>
        <p>For support or clarification, please contact <span className="font-bold text-blue-600">{supportEmail}</span>.</p>
      </div>
    ),
    cookies: (
      <div className="space-y-8">
        <div className="border-b border-slate-100 pb-6">
          <h2 className="text-3xl font-black text-slate-900 mb-2">Cookie Policy</h2>
          <p className="text-slate-500 text-sm italic">Transparency in Session Management</p>
        </div>
        <p className="text-slate-600">We use minimal cookies to ensure the stability of your analyst terminal. We do not use cookies for behavioral advertising.</p>
      </div>
    )
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row">
      {/* Sidebar Navigation */}
      <div className="w-full md:w-80 bg-white border-r border-slate-200 p-8 flex flex-col">
        <button onClick={onBack} className="flex items-center space-x-2 text-slate-400 hover:text-slate-900 transition-colors mb-12 group">
          <SafeIcon icon={FiArrowLeft} className="group-hover:-translate-x-1 transition-transform" />
          <span className="text-xs font-black uppercase tracking-widest">Back to Terminal</span>
        </button>
        <div className="space-y-2 flex-1">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === tab.id ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'text-slate-500 hover:bg-slate-50'}`}>
              <SafeIcon icon={tab.icon} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Content Area */}
      <div className="flex-1 p-8 md:p-20 overflow-y-auto">
        <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto bg-white p-12 rounded-[2.5rem] border border-slate-200 shadow-sm">
          {content[activeTab]}
        </motion.div>
      </div>
    </div>
  );
};

export default LegalPages;